import React, { useState } from 'react';
import MultiSelectTicker from '../MultiSelectTicker';

function ElasticityForm({ onSubmit, processing }) {
  const [formData, setFormData] = useState({
    tickers: [],
    optionType: 'call',
    strikePositions: [0], // ATM by default
    holdingPeriodType: 'percentage',
    holdingPeriodValue: 50, // 50% of time to expiration
  });

  // Strike position options with labels
  const strikeOptions = [
    { value: -0.20, label: '-20% ITM' },
    { value: -0.10, label: '-10% ITM' },
    { value: 0, label: 'ATM' },
    { value: 0.10, label: '+10% OTM' },
    { value: 0.20, label: '+20% OTM' },
    { value: 0.50, label: '+50% OTM' },
    { value: 1.00, label: '+100% OTM' },
  ];

  // Popular tickers for quick selection
  const popularTickers = ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'AMD'];

  const handleTickerChange = (selectedOptions) => {
    setFormData(prev => ({
      ...prev,
      tickers: selectedOptions || []
    }));
  };

  const handlePopularTickerClick = (ticker) => {
    const exists = formData.tickers.some(t => t.value === ticker);
    if (!exists) {
      setFormData(prev => ({
        ...prev,
        tickers: [...prev.tickers, { label: ticker, value: ticker }]
      }));
    }
  };

  const handleOptionTypeChange = (type) => {
    setFormData(prev => ({ ...prev, optionType: type }));
  };

  const handleStrikeToggle = (strikeValue) => {
    setFormData(prev => {
      const strikes = [...prev.strikePositions];
      const index = strikes.indexOf(strikeValue);
      if (index > -1) {
        strikes.splice(index, 1);
      } else {
        strikes.push(strikeValue);
      }
      return { ...prev, strikePositions: strikes };
    });
  };

  const handleHoldingPeriodTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      holdingPeriodType: type,
      holdingPeriodValue: type === 'percentage' ? 50 : 30
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.tickers.length === 0) {
      alert('Please select at least one ticker');
      return;
    }

    if (formData.strikePositions.length === 0) {
      alert('Please select at least one strike position');
      return;
    }

    // Convert to API format
    const requestData = {
      tickers: formData.tickers.map(t => t.value),
      option_type: formData.optionType,
      strike_positions: formData.strikePositions,
      holding_period_type: formData.holdingPeriodType,
      holding_period_value: formData.holdingPeriodType === 'percentage'
        ? formData.holdingPeriodValue / 100
        : formData.holdingPeriodValue
    };

    onSubmit(requestData);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white mb-4">Options Elasticity Analysis</h2>
        <p className="text-gray-400 mb-6">
          Analyze how options contracts respond to underlying price movements.
          See the leverage (multiplier) for each contract at various price scenarios.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Ticker Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Ticker Symbols *
          </label>
          <MultiSelectTicker
            value={formData.tickers}
            onChange={handleTickerChange}
            placeholder="Search and select tickers..."
          />

          {/* Quick Selection */}
          <div className="mt-3">
            <div className="text-xs text-gray-400 mb-2">Quick Selection:</div>
            <div className="flex flex-wrap gap-2">
              {popularTickers.map(ticker => (
                <button
                  key={ticker}
                  type="button"
                  onClick={() => handlePopularTickerClick(ticker)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    formData.tickers.some(t => t.value === ticker)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                  disabled={processing}
                >
                  {ticker}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Option Type */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Option Type *
          </label>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => handleOptionTypeChange('call')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                formData.optionType === 'call'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              disabled={processing}
            >
              Calls (Bullish)
            </button>
            <button
              type="button"
              onClick={() => handleOptionTypeChange('put')}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                formData.optionType === 'put'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              disabled={processing}
            >
              Puts (Bearish)
            </button>
          </div>
        </div>

        {/* Strike Positions */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Strike Positions * (select one or more)
          </label>
          <div className="flex flex-wrap gap-2">
            {strikeOptions.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleStrikeToggle(option.value)}
                className={`px-3 py-2 text-sm rounded-md font-medium transition-colors ${
                  formData.strikePositions.includes(option.value)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                disabled={processing}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            ITM = In The Money, ATM = At The Money, OTM = Out of The Money
          </p>
        </div>

        {/* Holding Period */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Holding Period
          </label>

          {/* Period Type Selection */}
          <div className="flex gap-4 mb-3">
            <button
              type="button"
              onClick={() => handleHoldingPeriodTypeChange('percentage')}
              className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${
                formData.holdingPeriodType === 'percentage'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              disabled={processing}
            >
              % of Expiration
            </button>
            <button
              type="button"
              onClick={() => handleHoldingPeriodTypeChange('fixed')}
              className={`px-4 py-2 text-sm rounded-md font-medium transition-colors ${
                formData.holdingPeriodType === 'fixed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              disabled={processing}
            >
              Fixed Days
            </button>
          </div>

          {/* Period Value Input */}
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={formData.holdingPeriodValue}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                holdingPeriodValue: Number(e.target.value)
              }))}
              min={formData.holdingPeriodType === 'percentage' ? 10 : 1}
              max={formData.holdingPeriodType === 'percentage' ? 90 : 365}
              className="w-24 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={processing}
            />
            <span className="text-gray-400">
              {formData.holdingPeriodType === 'percentage'
                ? '% of time to expiration'
                : 'days'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {formData.holdingPeriodType === 'percentage'
              ? 'e.g., 50% means if expiration is 90 days out, analyze at 45 days held'
              : 'Fixed number of days to hold the option'}
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-gray-700/50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">How Elasticity Works</h4>
          <ul className="text-xs text-gray-400 space-y-1">
            <li>• <strong>Multiplier</strong> = (Option % Gain) / (Underlying % Gain)</li>
            <li>• A multiplier of 3.5x means if the stock moves +10%, the option gains ~35%</li>
            <li>• OTM options have higher multipliers but lower probability of profit</li>
            <li>• Longer expirations have lower multipliers but more time for the move</li>
          </ul>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={processing || formData.tickers.length === 0 || formData.strikePositions.length === 0}
            className="w-full md:w-auto px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {processing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Analyzing Elasticity...
              </div>
            ) : (
              'Analyze Elasticity'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ElasticityForm;
